import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { DictionaryModel, HxDictionaryService, SaveDictionaryRequest, UiError, UiLabel } from 'hx-services';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { HxBreadcrumbsComponent } from '@adm-web-app/components/breadcrumbs/breadcrumbs.component';
import { NzPageHeaderComponent, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzSkeletonComponent } from 'ng-zorro-antd/skeleton';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzSwitchComponent } from 'ng-zorro-antd/switch';
import { HxLabelEditComponent } from 'hx-components';
import { NgIf } from '@angular/common';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';

import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzTagComponent } from 'ng-zorro-antd/tag';

@Component({
    selector: 'hc-dictionary-edit',
    templateUrl: './dictionary-edit.component.html',
    styleUrls: ['./dictionary-edit.component.less'],
  imports: [NzAffixComponent, HxBreadcrumbsComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective, TranslocoDirective, NzSkeletonComponent, FormsModule, NzFormDirective, ReactiveFormsModule, NzRowDirective, NzColDirective, NzFormItemComponent, NzFormLabelComponent, NzFormControlComponent, NzInputDirective, NzSwitchComponent, HxLabelEditComponent, NgIf, NzAlertComponent, NzButtonComponent, NzWaveDirective, NzIconDirective, NzTagComponent, TranslocoPipe]
})
export class DictionaryEditComponent implements OnInit, OnDestroy {
  dictionary?: DictionaryModel;
  isEdit = false;
  isCreate = false;
  form = new FormGroup({
    id: new FormControl<number | null>(null),
    title: new FormControl<UiLabel | null>(null, Validators.required),
    enabled: new FormControl<boolean | null>(null, Validators.required),
    key: new FormControl<string | null>(null, Validators.required),
    description: new FormControl<string | null>(null),
  });
  isLoading = {
    dictionary: false,
  };
  isSaving = false;
  private readonly path = 'dictionaries';
  private $destroyed = new Subject<void>();

  constructor(
    protected aRoute: ActivatedRoute,
    protected dictionaryService: HxDictionaryService,
    protected router: Router,
    protected modal: NzModalService,
    protected notification: NzNotificationService,
    protected tr: TranslocoService,
  ) {
  }

  ngOnInit(): void {
    const routeData: Data = this.aRoute.snapshot.data;
    this.dictionary = routeData['dictionary'] as DictionaryModel;
    if (this.dictionary) {
      this.isEdit = true;
    } else {
      this.isCreate = true;
    }
    this.updateForm();
  }

  ngOnDestroy(): void {
    this.$destroyed.next();
    this.$destroyed.complete();
  }

  async submitForm() {
    Object.values(this.form.controls).forEach(ctrl => {
      ctrl.markAsDirty();
      ctrl.updateValueAndValidity();
    });
    if (this.form.valid) {
      const formModel = this.form.value;
      if (!formModel.key || !formModel.title || formModel.enabled === null || formModel.enabled === undefined) {
        throw new UiError('form.required.fields');
      }
      const req: SaveDictionaryRequest = {
        id: this.dictionary?.id,
        enabled: formModel.enabled,
        title: formModel.title,
        key: formModel.key,
        description: formModel.description ?? undefined,
      };

      this.isSaving = true;

      try {
        const result = await this.dictionaryService.saveDictionary(req);
        this.form.markAsPristine();
        this.notification.create('success', this.tr.translate('nav.Dictionaries'), this.tr.translate('nt.dataSaved'));
        if (this.isCreate) {
          await this.router.navigate([`/${this.path}/${result.id}/edit`]);
        } else {
          this.isLoading.dictionary = true;
          this.dictionary = await this.dictionaryService.getDictionaryById(result.id);
          this.updateForm();
        }
      } catch (err: any) {
        this.notification.error(this.tr.translate('nav.Dictionaries'), this.tr.translate(err.error?.message));
      } finally {
        this.isLoading.dictionary = false;
        this.isSaving = false;
      }
    } else {
      Object.entries(this.form.controls).forEach(([key, control]) => {
        if (control.status !== 'VALID') {
          console.log('control is not valid', key, control);
        }
      });
      console.log('not valid form', this.form);
    }
  }

  private updateForm() {
    if (!this.dictionary) {
      return;
    }
    this.form.patchValue({
      id: this.dictionary.id,
      title: this.dictionary.title,
      enabled: this.dictionary.enabled,
      key: this.dictionary.key,
      description: this.dictionary.description,
    });
  }
}
